/**
 * WebSocketWrapper - Auth-aware WebSocket Provider Wrapper
 *
 * Only establishes WebSocket connection when user is authenticated.
 * Provides graceful degradation when WebSocket is unavailable.
 */

import { ReactNode, useMemo } from 'react';
import { WebSocketProvider } from '@teletienda/ws-client/react';
import { useAuth } from '@teletienda/shared-auth-ui';
import { useWsUrl } from '@/providers/PlatformBootstrapProvider';

interface WebSocketWrapperProps {
  children: ReactNode;
}

/**
 * Auth-aware WebSocket wrapper component
 *
 * - Only renders WebSocketProvider when user is authenticated
 * - Automatically passes JWT token to WebSocket connection
 * - Gracefully degrades to no WebSocket for unauthenticated users
 *
 * @example
 * ```tsx
 * <AuthProvider apiBaseUrl={AUTH_API_URL}>
 *   <WebSocketWrapper>
 *     <App />
 *   </WebSocketWrapper>
 * </AuthProvider>
 * ```
 */
export function WebSocketWrapper({ children }: WebSocketWrapperProps): React.ReactElement {
  const { isAuthenticated, token } = useAuth();
  const WS_URL = useWsUrl();

  // Debug logging
  console.log('[WebSocketWrapper] State:', {
    isAuthenticated,
    hasToken: !!token,
    tokenLength: token?.length,
    wsUrl: WS_URL,
  });

  // Memoize options to prevent unnecessary reconnections
  const wsOptions = useMemo(
    () => ({
      url: WS_URL,
      token: token || '',
    }),
    [token]
  );

  const shouldConnect = isAuthenticated && !!token;

  console.log('[WebSocketWrapper] Rendering WebSocketProvider:', {
    autoConnect: shouldConnect,
  });

  return (
    <WebSocketProvider options={wsOptions} autoConnect={shouldConnect}>
      {children}
    </WebSocketProvider>
  );
}
